import React, { useRef, useState } from 'react';
import identity from 'lodash/identity';
import { Controller, get, useFormContext } from 'react-hook-form';
import uniqueId from 'lodash/uniqueId'

import { Text } from '@src/components/Typography';
import { FileFieldProps } from './interface';
import { Box } from '@src/components/Box';
import { HStack } from '@src/components/HStack';
import CloudUpload from '@src/components/Icon/SVG/CloudUpload';
import { Icon } from '@src/components/Icon';
import { ErrorMessage } from '@hookform/error-message';
import { VStack } from '@src/components/VStack';

const stopDefaults = (e: React.DragEvent) => {
    e.stopPropagation();
    e.preventDefault();
};

export const FileField = (props: FileFieldProps) => {
    const {
        acceptedFileTypes,
        dropLabel = 'Drop file here',
        formatOnChange = identity,
        label = "Upload a file",
        name,
        parseValue = identity,
    } = props;
    const [hasAddedFileThisSession, setHasAddedFileThisSession] = useState(false);
    const [dragging, setDragging] = useState(false);
    const { control, formState: { errors }, } = useFormContext();

    const fieldErrors = get(errors, name);

    const parsedErrors = fieldErrors ? { [name]: fieldErrors } : {};

    const idRef = useRef(uniqueId())

    return (
        <Controller
            control={control}
            name={name}
            render={({ field: { onChange, value: fieldValue } }) => (
                <>
                    <VStack gap={2}>
                        <input
                            data-testid="file-input"
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const file = e.target?.files?.[0];
                                const formattedValue = formatOnChange(file, fieldValue);
                                onChange(formattedValue);
                                setHasAddedFileThisSession(true);
                            }}
                            accept={acceptedFileTypes.join(', ')}
                            style={{ display: 'none' }}
                            type="file"
                            id={idRef.current}
                        />
                        <label
                            htmlFor={idRef.current}
                            onDragEnter={(e: React.DragEvent) => {
                                stopDefaults(e);
                                setDragging(true);
                            }}
                            onDragLeave={(e: React.DragEvent) => {
                                if (!e.currentTarget.contains(e.relatedTarget as Node)) {
                                    stopDefaults(e);
                                    setDragging(false);
                                }
                            }}
                            onDragOver={stopDefaults}
                            onDrop={(e: React.DragEvent<HTMLElement>) => {
                                stopDefaults(e);
                                setDragging(false);
                                const file = e.dataTransfer?.files?.[0];
                                const formattedValue = formatOnChange(file, fieldValue);
                                onChange(formattedValue);
                                setHasAddedFileThisSession(true);
                            }}
                            style={{
                                cursor: 'pointer',
                                textAlign: 'center',
                                display: 'flex',
                            }}
                        >
                            <Box
                                px={1.5}
                                py={1.375}
                                width='100%'
                                borderWidth={1}
                                borderRadius='8px'
                                borderStyle='dashed'
                                borderColor='controls.outline'
                                bgColor={dragging ? 'surface.backing-1' : undefined}
                            >
                                <HStack width="100%" height="100%" alignItems="center">
                                    <CloudUpload />
                                    <VStack ml={2} alignItems='flex-start'>
                                        <Text size="m">{dragging ? dropLabel : label}</Text>
                                        <Text size="s">
                                            {`Supports ${acceptedFileTypes.join(', ')}`}
                                        </Text>
                                    </VStack>
                                </HStack>
                            </Box>
                        </label>
                        {parseValue(fieldValue) && (
                            <Box bgColor='surface.backing-1' py={1.25} pl={2} pr={2.5}>
                                <HStack justifyContent="space-between" alignItems="center">
                                    <Text size="m">{hasAddedFileThisSession ? parseValue(fieldValue).name : parseValue(fieldValue)}</Text>
                                    <Box onClick={() => {
                                        onChange(formatOnChange(null, fieldValue));
                                    }}>
                                        <Icon variant='custom' name="trash" bgColor={undefined} />
                                    </Box>
                                </HStack>
                            </Box>
                        )}
                    </VStack>
                    <ErrorMessage
                        name={name}
                        errors={parsedErrors}
                        render={({ message }) => (
                            <Text size="s" color="state.error.secondary">
                                {message}
                            </Text>
                        )}
                    />
                </>
            )
            }
        />
    );
};
